import {ChangeDetectionStrategy, Component, computed, inject, input, model} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ModuleFunction} from '@frontend/directus';
import {CountryEmployeesView, CoverageRequirementView} from '@frontend/shared-api';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {Dialog} from 'primeng/dialog';
import {getTranslation} from '../../../../../../../shared/utils/translation-utils';
import {RfiParticipatingTool} from '../step-5-3-rfi-invitation-list.types';
import {buildCoverageAnalysisView} from './rfi-coverage-analysis.model';
import {
  AnalysisTab,
  AnalysisTabItem,
  CoverageNameLookup,
} from './rfi-coverage-analysis-dialog.types';
import {CoverageOverviewTabComponent} from './coverage-overview-tab/coverage-overview-tab.component';
import {CoverageGapsTabComponent} from './coverage-gaps-tab/coverage-gaps-tab.component';
import {CoverageCountriesTabComponent} from './coverage-countries-tab/coverage-countries-tab.component';
import {CoverageMatrixTabComponent} from './coverage-matrix-tab/coverage-matrix-tab.component';
import {CoverageExtrasTabComponent} from './coverage-extras-tab/coverage-extras-tab.component';
import {CoverageCalculationTabComponent} from './coverage-calculation-tab/coverage-calculation-tab.component';

const TAB_BASE_CLASS = 'border-b-2 px-4 py-3 text-sm transition-colors';
const ACTIVE_TAB_CLASS = `${TAB_BASE_CLASS} border-[var(--color-button-red)] bg-rose-950 text-white`;
const INACTIVE_TAB_CLASS = `${TAB_BASE_CLASS} text-muted-color border-transparent hover:text-white`;
const TAB_LABEL_KEY_PREFIX = 'project.steps.rfi.invitationList.analysis.tab.';
const ANALYSIS_TABS: AnalysisTab[] = [
  'overview',
  'gaps',
  'countries',
  'matrix',
  'extras',
  'calculation',
];

@Component({
  selector: 'app-rfi-coverage-analysis-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    Button,
    Dialog,
    CoverageOverviewTabComponent,
    CoverageGapsTabComponent,
    CoverageCountriesTabComponent,
    CoverageMatrixTabComponent,
    CoverageExtrasTabComponent,
    CoverageCalculationTabComponent,
  ],
  templateUrl: './rfi-coverage-analysis-dialog.component.html',
  styleUrl: './rfi-coverage-analysis-dialog.component.scss',
})
export class RfiCoverageAnalysisDialogComponent {
  private readonly transloco = inject(TranslocoService);

  readonly visible = model(false);
  readonly activeTab = model<AnalysisTab>('overview');
  readonly tool = input<RfiParticipatingTool | null>(null);
  readonly requirements = input.required<CoverageRequirementView[]>();
  readonly countryEmployees = input.required<CountryEmployeesView[]>();
  readonly moduleFunctions = input.required<ModuleFunction[]>();

  private readonly language = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  protected readonly titleParams = computed(() => ({tool: this.tool()?.translatedName ?? ''}));
  protected readonly view = computed(() => {
    const coverage = this.tool()?.coverage?.details;
    return coverage
      ? buildCoverageAnalysisView(coverage, this.countryEmployees(), this.nameLookup())
      : null;
  });
  protected readonly tabItems = computed((): AnalysisTabItem[] => {
    const activeTab = this.activeTab();
    const gapCount = this.view()?.gaps.length ?? 0;
    return ANALYSIS_TABS.map((tab) => ({
      id: tab,
      labelKey: TAB_LABEL_KEY_PREFIX + tab,
      count: tab === 'gaps' ? gapCount : null,
      active: tab === activeTab,
      cssClass: tab === activeTab ? ACTIVE_TAB_CLASS : INACTIVE_TAB_CLASS,
    }));
  });
  private readonly nameLookup = computed((): CoverageNameLookup => {
    const functionsById = new Map(
      this.moduleFunctions().map((moduleFunction) => [moduleFunction.id, moduleFunction]),
    );
    const language = this.language();
    const countryNames = this.createCountryNames(language);
    return {
      functionName: (moduleFunctionId) =>
        this.translatedFunctionName(
          functionsById.get(moduleFunctionId),
          moduleFunctionId,
          language,
        ),
      countryName: (countryIsoCode) => this.countryName(countryNames, countryIsoCode),
    };
  });

  protected showTab(tab: AnalysisTab): void {
    this.activeTab.set(tab);
  }

  protected close(): void {
    this.visible.set(false);
  }

  private translatedFunctionName(
    moduleFunction: ModuleFunction | undefined,
    moduleFunctionId: number,
    language: string,
  ): string {
    if (!moduleFunction) {
      return `${moduleFunctionId}`;
    }
    return (
      getTranslation(moduleFunction.translations, language)?.name?.trim() || moduleFunction.name
    );
  }

  private countryName(countryNames: Intl.DisplayNames | null, countryIsoCode: string): string {
    try {
      return countryNames?.of(countryIsoCode) ?? countryIsoCode;
    } catch {
      return countryIsoCode;
    }
  }

  private createCountryNames(language: string): Intl.DisplayNames | null {
    try {
      return new Intl.DisplayNames([language], {type: 'region'});
    } catch {
      return null;
    }
  }
}
